import { KyInstance, type Options } from 'ky';
import { Cohort } from '~src/types/user';
import { z } from 'zod';
import { Uuid, UuidString } from '~src/utils/UUID';

export class UserClient {
  private http_client: KyInstance;
  constructor(http_client: KyInstance) {
    this.http_client = http_client;
  }

  async signup(email: string, password?: string, config: Options = {}) {
    await this.http_client.post('users/signup', {
      json: { email, password },
      ...config,
    });
  }
  async signin(email: string, password: string, remember_session: boolean = false, config: Options = {}) {
    await this.http_client.post('users/signin', {
      json: { credentials: { email, password }, remember_session },
      ...config,
    });
  }

  async signinWithToken(token: string, remember_session: boolean = false, config: Options = {}) {
    await this.http_client.post('users/signin', {
      json: { token, remember_session },
      ...config,
    });
  }

  async requestMagicLink(email: string, config: Options = {}) {
    await this.http_client.post('users/request-magic-link', {
      json: { email },
      ...config,
    });
  }

  async requestPasswordReset(email: string, config: Options = {}) {
    await this.http_client.post('users/request-password-reset', {
      json: { email },
      ...config,
    });
  }

  async resetPassword(token: string, new_password: string, config: Options = {}) {
    await this.http_client.post('users/reset-password', {
      json: { token, new_password },
      ...config,
    });
  }

  async signout(config: Options = {}) {
    await this.http_client.get('users/signout', { ...config });
  }

  async whoami(config: Options = {}): Promise<UuidString> {
    const res = (await this.http_client.get('users/whoami', { ...config }).json()) as {
      user_id: string;
    };
    return Uuid.from_string(res.user_id);
  }

  async getCohort(config: Options = {}): Promise<Cohort> {
    const response = CohortResponse.parse(await this.http_client.get('users/cohort', { ...config }).json());
    return response.cohort;
  }
}

const CohortResponse = z.object({
  cohort: Cohort,
});
